import { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFilePdf,
  faImage,
  faFileCsv,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { useExportDashboard } from '../hooks/useDashboards';
import styles from './ExportMenu.module.css';

interface ExportMenuProps {
  dashboardId: number;
  dashboardName: string;
  onSchedule: () => void;
}

export default function ExportMenu({ dashboardId, dashboardName, onSchedule }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const exportMut = useExportDashboard();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const download = useCallback(
    (format: 'pdf' | 'png' | 'csv') => {
      exportMut.mutate(
        { dashboardId, format },
        {
          onSuccess: ({ blob, ext }) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${dashboardName.replace(/[^a-zA-Z0-9_-]/g, '_')}.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setOpen(false);
          },
        },
      );
    },
    [dashboardId, dashboardName, exportMut],
  );

  const exporting = exportMut.isPending;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={styles.triggerBtn}
        onClick={() => setOpen((v) => !v)}
        title="Export / Schedule"
      >
        {exporting ? <span className={styles.spinner} /> : <FontAwesomeIcon icon={faDownload} />}
        Export
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Export now</div>
            <button className={styles.item} onClick={() => download('pdf')} disabled={exporting}>
              <FontAwesomeIcon icon={faFilePdf} className={styles.itemIcon} />
              Download PDF
            </button>
            <button className={styles.item} onClick={() => download('png')} disabled={exporting}>
              <FontAwesomeIcon icon={faImage} className={styles.itemIcon} />
              Download PNG
            </button>
            <button className={styles.item} onClick={() => download('csv')} disabled={exporting}>
              <FontAwesomeIcon icon={faFileCsv} className={styles.itemIcon} />
              Download CSV
            </button>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Automate</div>
            <button
              className={styles.item}
              onClick={() => {
                setOpen(false);
                onSchedule();
              }}
            >
              <FontAwesomeIcon icon={faClock} className={styles.itemIcon} />
              Schedule recurring report
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
